import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, MapPin, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { SectionHeading } from "@/components/Reveal";
import { CITIES } from "@/constants/brand";
import { api, formatApiError } from "@/lib/api";

const CONSENTS = [
  { key: "consenso_auto", label: "Sono consapevole che per le consegne dovrò utilizzare la mia auto", testid: "consent-auto-checkbox" },
  { key: "consenso_weekend", label: "Sono consapevole che si lavora anche durante i weekend", testid: "consent-weekend-checkbox" },
  { key: "consenso_compenso", label: "Sono consapevole che non si tratta di uno stipendio fisso, ma variabile in base alle consegne/turni", testid: "consent-compenso-checkbox" },
];

const CITY_IDS = ["city-option-cento", "city-option-persiceto", "city-option-san-pietro"];

const inputCls =
  "w-full bg-surface border border-input focus:border-brand rounded-xl px-4 py-3.5 text-white placeholder:text-dim outline-none transition-colors duration-300 text-base";

const INITIAL = {
  nome: "", cognome: "", email: "", telefono: "", eta: "", residenza: "", citta: "",
  consenso_auto: false, consenso_weekend: false, consenso_compenso: false,
};

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="block text-sm font-bold text-white mb-2">{label}</label>
    <input data-testid={testid} className={inputCls} {...props} />
  </div>
);

export const ApplicationForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.citta) {
      toast.error("Seleziona la città in cui vuoi lavorare");
      return;
    }
    if (!form.consenso_auto || !form.consenso_weekend || !form.consenso_compenso) {
      toast.error("Devi confermare tutte le informazioni di presa visione");
      return;
    }
    setSending(true);
    try {
      await api.post("/applications", { ...form, eta: parseInt(form.eta, 10) });
      setDone(true);
      toast.success("Candidatura inviata! Ti abbiamo inviato un'email di conferma.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="candidati" data-testid="application-section" className="relative py-24 sm:py-32 px-5 sm:px-8 bg-surface overflow-hidden">
      <div className="hero-glow w-[460px] h-[460px] bg-brand/12 -bottom-40 -right-40" />
      <div className="relative max-w-3xl mx-auto">
        <SectionHeading
          eyebrow="04 — Candidati"
          title="È il momento di conoscerci di persona"
          sub="Se quello che hai visto ti piace, compila il modulo sottostante e sarai ricontattato il prima possibile per un primo colloquio conoscitivo."
          testid="application-title"
        />

        <AnimatePresence mode="wait">
          {done ? (
            <motion.div
              key="success"
              data-testid="application-success"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="bg-card2 border border-brand/40 rounded-3xl p-10 sm:p-14 text-center"
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-brand/15 border border-brand/40 flex items-center justify-center mb-6">
                <PartyPopper className="w-7 h-7 text-brand" />
              </div>
              <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-white mb-4">
                Candidatura ricevuta!
              </h3>
              <p className="text-muted2 leading-relaxed max-w-md mx-auto">
                Grazie per il tuo interesse. Controlla la tua casella email: ti abbiamo inviato la conferma di
                avvenuta registrazione. Ti ricontatteremo presto per il colloquio conoscitivo.
              </p>
              <button
                data-testid="application-reset-btn"
                onClick={() => { setForm(INITIAL); setDone(false); }}
                className="mt-8 inline-flex items-center gap-2 border border-brand/40 hover:border-brand hover:bg-cardhover text-white font-bold px-6 py-3 rounded-full transition-colors duration-300"
              >
                Invia un'altra candidatura
              </button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              data-testid="application-form"
              onSubmit={submit}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="bg-card2/70 backdrop-blur-md border border-brand/20 rounded-3xl p-6 sm:p-10 space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Nome *" testid="apply-nome-input" required value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Mario" />
                <Field label="Cognome *" testid="apply-cognome-input" required value={form.cognome} onChange={(e) => set("cognome", e.target.value)} placeholder="Rossi" />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Email *" testid="apply-email-input" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="mario.rossi@email.it" />
                <Field label="Telefono *" testid="apply-telefono-input" type="tel" required value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="+39 333 1234567" />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Età *" testid="apply-eta-input" type="number" min="18" max="99" required value={form.eta} onChange={(e) => set("eta", e.target.value)} placeholder="18+" />
                <Field label="Dove abiti? *" testid="apply-residenza-input" required value={form.residenza} onChange={(e) => set("residenza", e.target.value)} placeholder="Città / Indirizzo di residenza" />
              </div>

              <div>
                <label className="block text-sm font-bold text-white mb-3">In quale città vuoi lavorare? *</label>
                <div className="grid sm:grid-cols-3 gap-3">
                  {CITIES.map((c, i) => (
                    <button
                      type="button"
                      key={c}
                      data-testid={CITY_IDS[i]}
                      onClick={() => set("citta", c)}
                      className={`flex items-center gap-2.5 rounded-xl border px-4 py-3.5 text-left font-semibold text-sm transition-colors duration-300 ${
                        form.citta === c
                          ? "border-brand bg-brand/15 text-white"
                          : "border-input bg-surface text-muted2 hover:border-brand/50 hover:text-white"
                      }`}
                    >
                      <MapPin className={`w-4 h-4 shrink-0 ${form.citta === c ? "text-brand" : "text-dim"}`} />
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-white mb-3">
                  Informazioni importanti e presa visione *
                </label>
                <div className="space-y-3">
                  {CONSENTS.map((c) => (
                    <button
                      type="button"
                      key={c.key}
                      data-testid={c.testid}
                      onClick={() => set(c.key, !form[c.key])}
                      className={`w-full flex items-start gap-3.5 rounded-xl border px-4 py-3.5 text-left transition-colors duration-300 ${
                        form[c.key] ? "border-brand/60 bg-brand/10" : "border-input bg-surface hover:border-brand/40"
                      }`}
                    >
                      <span
                        className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors duration-300 ${
                          form[c.key] ? "bg-brand border-brand" : "border-dim"
                        }`}
                      >
                        {form[c.key] && <Check className="w-3.5 h-3.5 text-white" />}
                      </span>
                      <span className="text-sm leading-relaxed text-white/85">{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <motion.button
                data-testid="submit-candidatura-btn"
                type="submit"
                disabled={sending}
                whileTap={{ scale: 0.97 }}
                className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hot disabled:opacity-60 text-white font-display font-bold text-lg px-8 py-4.5 py-4 rounded-full transition-colors duration-300 shadow-[0_10px_30px_-5px_rgba(232,67,147,0.45)]"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Invio in corso...
                  </>
                ) : (
                  "Invia Candidatura"
                )}
              </motion.button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
